import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoletaCanvas from '../../Components/Roleta';
import './Roleta.scss';

// const PAINEL_URL = 'https://painel.tribodasorteonline.com.br';
const PAINEL_URL = 'http://localhost:3000';

export default function Roleta() {
  const { user } = useAuth();
  const [premios, setPremios] = useState([]);
  const [config, setConfig] = useState(null);
  const [girosDisponiveis, setGirosDisponiveis] = useState(0);
  const [girando, setGirando] = useState(false);
  const [anguloFinal, setAnguloFinal] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [giroId, setGiroId] = useState(null);
  const [precisaResgate, setPrecisaResgate] = useState(false);
  const [showResultado, setShowResultado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  // Form de resgate (pix/fisico)
  const [whatsapp, setWhatsapp] = useState('');
  const [chavePix, setChavePix] = useState('');
  const [enviandoResgate, setEnviandoResgate] = useState(false);
  const [resgateEnviado, setResgateEnviado] = useState(false);

  const token = localStorage.getItem('tribodasorte');

  async function carregar() {
    try {
      const [cfgRes, premiosRes] = await Promise.all([
        fetch(`${PAINEL_URL}/api/roleta/configuracao`),
        fetch(`${PAINEL_URL}/api/roleta/premios?disponiveis=true`),
      ]);

      const cfg = await cfgRes.json();
      const prems = await premiosRes.json();

      setConfig(cfg);
      setPremios(prems);

      if (cfg.ativo && user) {
        const girosRes = await fetch(
          `https://api.tribodasorteonline.com.br/api/roleta/giros_usuario/${user.id}`,
          // `http://127.0.0.1:8000/api/roleta/giros_usuario/${user.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const girosData = await girosRes.json();
        setGirosDisponiveis(girosData.giros_disponiveis || 0);
      }
    } catch (e) {
      setErro('Erro ao carregar a roleta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
  }, [user]);

  function calcularAnguloDoVencedor(premioId, premiosAtivos) {
    // Segmentos visuais são todos iguais (independente da probabilidade)
    const angulo = (2 * Math.PI) / premiosAtivos.length;
    const idx = premiosAtivos.findIndex((p) => p.id === premioId);
    if (idx === -1) return 0;

    // No desenho, segmentos começam em -PI/2 (topo) indo no sentido horário.
    // O ponteiro também aponta para -PI/2.
    // Posição do meio do segmento idx, antes de qualquer rotação:
    //   meioAbsoluto = -PI/2 + (idx + 0.5) * angulo
    // Queremos que, após rotação R, esse meio caia em -PI/2:
    //   meioAbsoluto + R = -PI/2  =>  R = -(idx + 0.5) * angulo
    const R = -(idx + 0.5) * angulo;
    return ((R % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  }

  async function girar() {
    if (girando || girosDisponiveis <= 0) return;
    setGirando(true);
    setShowResultado(false);
    setResultado(null);
    setGiroId(null);
    setPrecisaResgate(false);
    setResgateEnviado(false);
    setWhatsapp('');
    setChavePix('');
    setErro('');

    try {
      const res = await fetch(`${PAINEL_URL}/api/roleta/girar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuario_id: user?.id, token }),
      });

      const data = await res.json();

      if (!res.ok) {
        setErro(data.message || 'Erro ao girar a roleta.');
        setGirando(false);
        return;
      }

      const angulo = calcularAnguloDoVencedor(data.premio.id, premios);
      setAnguloFinal(angulo);
      setResultado(data.premio);
      setGiroId(data.giro_id);
      setPrecisaResgate(!!data.precisa_resgate);
    } catch {
      setErro('Erro de conexão. Tente novamente.');
      setGirando(false);
    }
  }

  function onAnimacaoFim() {
    setGirando(false);
    setShowResultado(true);
    setGirosDisponiveis((g) => Math.max(0, g - 1));
  }

  async function enviarResgate() {
    if (!whatsapp || whatsapp.trim().length < 8) {
      setErro('Informe um WhatsApp válido.');
      return;
    }
    if (resultado?.tipo === 'pix' && (!chavePix || chavePix.trim().length < 3)) {
      setErro('Informe sua chave PIX.');
      return;
    }
    setEnviandoResgate(true);
    setErro('');
    try {
      const res = await fetch(`${PAINEL_URL}/api/roleta/giros/${giroId}/resgate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          whatsapp: whatsapp.trim(),
          chave_pix: chavePix.trim() || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        setErro(data.message || 'Erro ao enviar.');
        return;
      }
      setResgateEnviado(true);
    } catch {
      setErro('Erro de conexão. Tente novamente.');
    } finally {
      setEnviandoResgate(false);
    }
  }

  if (loading) {
    return (
      <div className="roleta-page roleta-page--loading">
        <div className="roleta-spinner" />
        <p>Carregando roleta...</p>
      </div>
    );
  }

  if (!config?.ativo) {
    return (
      <div className="roleta-page roleta-page--inativa">
        <div className="roleta-inativa-card">
          <span className="roleta-inativa-icon">🎡</span>
          <h2>Roleta indisponível</h2>
          <p>A roleta está temporariamente desativada. Fique de olho nas novidades!</p>
        </div>
      </div>
    );
  }

  if (premios.length === 0) {
    return (
      <div className="roleta-page roleta-page--inativa">
        <div className="roleta-inativa-card">
          <span className="roleta-inativa-icon">🎡</span>
          <h2>Em breve!</h2>
          <p>Os prêmios da roleta estão sendo preparados.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="roleta-page">
      <div className="roleta-header">
        <h1 className="roleta-titulo">🎡 Roleta da Sorte</h1>
        <p className="roleta-subtitulo">
          Compre <strong>R$ {config?.valor_minimo_compra?.toFixed(2).replace('.', ',')}</strong> em
          bolões e ganhe 1 giro!
        </p>
      </div>

      <div className="roleta-container">
        <div className="roleta-area">
          <RoletaCanvas
            premios={premios}
            girando={girando}
            anguloFinal={anguloFinal}
            onAnimacaoFim={onAnimacaoFim}
          />

          <div className="roleta-giros-info">
            <span className="roleta-giros-badge">
              {girosDisponiveis} giro{girosDisponiveis !== 1 ? 's' : ''} disponível
              {girosDisponiveis !== 1 ? 'is' : ''}
            </span>
          </div>

          {erro && <p className="roleta-erro">{erro}</p>}

          <button
            className={`roleta-btn-girar ${girando ? 'roleta-btn-girar--girando' : ''} ${
              girosDisponiveis <= 0 ? 'roleta-btn-girar--disabled' : ''
            }`}
            onClick={girar}
            disabled={girando || girosDisponiveis <= 0}
          >
            {girando ? 'Girando...' : girosDisponiveis > 0 ? '🎰 GIRAR!' : 'Sem giros'}
          </button>
        </div>

        <div className="roleta-premios-lista">
          <h3>Prêmios disponíveis</h3>
          <ul>
            {premios.map((p) => (
              <li key={p.id} className="roleta-premio-item">
                <span className="roleta-premio-cor" style={{ backgroundColor: p.cor }} />
                <div>
                  <strong>{p.nome}</strong>
                  <small>
                    {p.quantidade_cotas} cota{p.quantidade_cotas !== 1 ? 's' : ''} · Bolão #{p.bolao_id}
                  </small>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {showResultado && resultado && (
        <div className="roleta-overlay" onClick={() => setShowResultado(false)}>
          <div className="roleta-modal" onClick={(e) => e.stopPropagation()}>
            {/* TIPO: VAZIO */}
            {resultado.tipo === 'vazio' && (
              <>
                <div className="roleta-modal-icon">😔</div>
                <h2>Não foi dessa vez!</h2>
                <div className="roleta-modal-premio roleta-modal-premio--vazio" style={{ borderColor: resultado.cor }}>
                  <span className="roleta-modal-cor" style={{ backgroundColor: resultado.cor }} />
                  <strong>{resultado.nome}</strong>
                </div>
                <p className="roleta-modal-info roleta-modal-info--vazio">
                  Continue comprando bolões e tente de novo!
                </p>
                <button className="roleta-modal-btn" onClick={() => setShowResultado(false)}>
                  Tente novamente
                </button>
              </>
            )}

            {/* TIPO: PREMIO (cotas) */}
            {resultado.tipo === 'premio' && (
              <>
                <div className="roleta-modal-icon">🎉</div>
                <h2>Parabéns!</h2>
                <p>Você ganhou:</p>
                <div className="roleta-modal-premio" style={{ borderColor: resultado.cor }}>
                  <span className="roleta-modal-cor" style={{ backgroundColor: resultado.cor }} />
                  <div>
                    <strong>{resultado.nome}</strong>
                    <span>
                      {resultado.quantidade_cotas} cota{resultado.quantidade_cotas !== 1 ? 's' : ''} do Bolão #{resultado.bolao_id}
                    </span>
                  </div>
                </div>
                <p className="roleta-modal-info">As cotas já foram adicionadas à sua conta!</p>
                <button className="roleta-modal-btn" onClick={() => setShowResultado(false)}>
                  Ótimo!
                </button>
              </>
            )}

            {/* TIPO: PIX ou FÍSICO — precisa de resgate */}
            {(resultado.tipo === 'pix' || resultado.tipo === 'fisico') && !resgateEnviado && (
              <>
                <div className="roleta-modal-icon">🎉</div>
                <h2>Parabéns, você ganhou!</h2>
                <div className="roleta-modal-premio" style={{ borderColor: resultado.cor }}>
                  <span className="roleta-modal-cor" style={{ backgroundColor: resultado.cor }} />
                  <div>
                    <strong>{resultado.nome}</strong>
                    {resultado.tipo === 'pix' && resultado.valor_pix && (
                      <span>R$ {Number(resultado.valor_pix).toFixed(2).replace('.', ',')} via PIX</span>
                    )}
                    {resultado.tipo === 'fisico' && <span>Prêmio físico</span>}
                  </div>
                </div>
                <p className="roleta-modal-info" style={{ marginTop: 12 }}>
                  {resultado.tipo === 'pix'
                    ? 'Informe sua chave PIX e WhatsApp para receber o valor:'
                    : 'Informe seu WhatsApp para entrarmos em contato:'}
                </p>

                <div className="roleta-form">
                  <label>
                    WhatsApp (com DDD) *
                    <input
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                    />
                  </label>

                  {resultado.tipo === 'pix' && (
                    <label>
                      Chave PIX *
                      <input
                        type="text"
                        placeholder="CPF, e-mail, telefone ou chave aleatória"
                        value={chavePix}
                        onChange={(e) => setChavePix(e.target.value)}
                      />
                    </label>
                  )}
                </div>

                {erro && <p className="roleta-erro" style={{ marginTop: 8 }}>{erro}</p>}

                <button
                  className="roleta-modal-btn"
                  onClick={enviarResgate}
                  disabled={enviandoResgate}
                >
                  {enviandoResgate ? 'Enviando...' : 'Confirmar e resgatar'}
                </button>
              </>
            )}

            {/* TIPO: PIX ou FÍSICO — resgate enviado */}
            {(resultado.tipo === 'pix' || resultado.tipo === 'fisico') && resgateEnviado && (
              <>
                <div className="roleta-modal-icon">✅</div>
                <h2>Resgate registrado!</h2>
                <p className="roleta-modal-info">
                  {resultado.tipo === 'pix'
                    ? 'O valor será enviado pra sua chave PIX em breve. Entraremos em contato pelo WhatsApp.'
                    : 'Entraremos em contato pelo WhatsApp para combinar a entrega do seu prêmio.'}
                </p>
                <button className="roleta-modal-btn" onClick={() => setShowResultado(false)}>
                  Ótimo!
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
